import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(
        `${API_URL}/api/auth/forgot-password`,
        { email },
        { withCredentials: true }
      );
      
      setSubmitted(true);
      toast.success('Password reset link sent to your email!');
    } catch (error) {
      console.error('Forgot password error:', error);
      toast.error(error.response?.data?.detail || 'Failed to send reset link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream px-4 sm:px-6">
        <div className="w-full max-w-md">
          <div className="text-center mb-6 sm:mb-8">
            <Link to="/">
              <img 
                src="https://customer-assets.emergentagent.com/job_haute-assets/artifacts/jb59wbkx_THC%20Final%20Logo%20%28Cutout%202%29.png" 
                alt="The Haute Circle" 
                className="h-32 sm:h-40 mx-auto mb-4 sm:mb-6" 
              />
            </Link>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-6 sm:p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
              </svg>
            </div>
            <h2 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4">Check Your Email</h2>
            <p className="text-gray-600 mb-4 sm:mb-6 text-sm sm:text-base">
              We've sent a password reset link to <strong>{email}</strong>.
              Please check your inbox and follow the instructions.
            </p>
            <Button 
              onClick={() => navigate('/login')}
              className="bg-black text-white hover:bg-black/90 w-full sm:w-auto px-8 min-h-[48px]"
            >
              Back to Login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-cream px-4 sm:px-6 py-6 sm:py-8">
      <div className="w-full max-w-md">
        {/* Back Button */}
        <Button 
          onClick={() => navigate('/login')}
          className="mb-4 sm:mb-6 border border-black bg-white text-black hover:bg-black hover:text-white transition-all text-sm sm:text-base min-h-[44px]"
        >
          ← Back to Login
        </Button>
        
        <div className="text-center mb-6 sm:mb-8">
          <Link to="/">
            <img 
              src="https://customer-assets.emergentagent.com/job_haute-assets/artifacts/jb59wbkx_THC%20Final%20Logo%20%28Cutout%202%29.png" 
              alt="The Haute Circle" 
              className="h-32 sm:h-40 mx-auto mb-4 sm:mb-6" 
            />
          </Link>
          <h1 className="text-2xl sm:text-3xl font-bold mb-2">Forgot Password?</h1>
          <p className="text-gray-600 text-xs sm:text-sm">Enter your email to receive a reset link</p>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6 sm:p-8">
          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
            <div>
              <label className="block text-sm font-medium mb-2">Email Address</label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="Enter your email"
                className="w-full min-h-[44px]"
              />
            </div>

            <Button 
              type="submit" 
              className="w-full bg-black text-white hover:bg-black/90 min-h-[48px] text-sm sm:text-base" 
              disabled={loading}
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </Button>
          </form>

          <p className="mt-4 sm:mt-6 text-center text-xs sm:text-sm">
            Remember your password?{' '}
            <Link to="/login" className="text-gold hover:underline font-medium">
              Login
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;